import { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  InputBase,
  Paper,
  Box,
  Popover,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Search as SearchIcon } from "@mui/icons-material";
import Spinner from "./Spinner";

const ReactTable = ({
  // title = "Data Table",
  columns,
  rows = [],
  rowKey = "_id",
  initialRowsPerPage = 5,
  headerColor,
  headerTextColor,
  loading,
  CurrentPage,
  // rowsPerPage,
  totalRows,
  // onPageChange,
  onRowsPerPageChange,
  initialOrderBy = columns[0]?.field || "",
  initialOrder = "asc",
}) => {


  const theme = useTheme();

  const [order, setOrder] = useState(initialOrder);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [filters, setFilters] = useState({});
  const [anchorEls, setAnchorEls] = useState({});

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleOpenSearch = (event, field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleCloseSearch = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  const handleSearchChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value.toLowerCase() }));
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      Object.entries(filters).every(([field, value]) => {
        const col = columns.find((c) => c.field === field);

        const searchValue = col?.searchKey
          ? col.searchKey(row)
          : String(row[field] ?? "");

        return searchValue.toLowerCase().includes(value);
      }),
    );
  }, [rows, filters, columns]);

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const sortedRows = useMemo(() => {
    return orderBy
      ? stableSort(filteredRows, getComparator(order, orderBy))
      : filteredRows;
  }, [filteredRows, order, orderBy]);

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [sortedRows, page, rowsPerPage]);


  return (
    <Paper
      elevation={3}
      sx={{
        // p: 2,
        borderRadius: 3,
        border: "1px solid #C6C6C6",
        background: theme.palette.background.default,
      }}
    >

      { <Spinner loading={loading}/>}

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner />
        </Box>
      )}

      <TableContainer
        sx={{
          maxWidth: "100%",
          overflowX: "auto",
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(2,123,154,0.08)",
          border: "1px solid #C6C6C6",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#F2F9FF",
              }}
            >
              {columns.map((col) => {
                const isActive = orderBy === col.field;

                return (
                  <TableCell
                    key={col.field}
                    sx={{
                      fontWeight: 600,
                      background: "#AEDAFF",
                      color: theme.palette.secondary.main,
                      whiteSpace: "nowrap",
                      borderRight: "1px solid rgba(255,255,255,0.2)",
                      "&:last-child": { borderRight: "none" },
                      userSelect: "none",
                    }}
                    sortDirection={isActive ? order : false}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minWidth: col.minWidth || 120,
                      }}
                    >
                      {!col.disableSort ? (
                        <TableSortLabel
                          active={isActive}
                          direction={isActive ? order : "asc"}
                          onClick={() => handleSort(col.field)}
                          sx={{
                            gap: "6px",
                            color: "#000000",
                            "&.Mui-active": {
                              color: "#000000",
                            },
                            "&:hover": {
                              color: "#000000",
                            },
                            ".MuiTableSortLabel-icon": {
                              fontSize: "1.3rem",
                              // fontWeight: "bold",
                              opacity: 1,
                              color: `${theme.palette.text.secondary} !important`,
                            },
                          }}
                        >
                          {col.headerName}
                        </TableSortLabel>
                      ) : (
                        <Box sx={{ color: "#000000" }}>
                          {col.headerName}
                        </Box>
                      )}

                      {!col.disableSearch && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenSearch(e, col.field)}
                            sx={{
                              color: "#000000",
                              ml: 0.5,
                            }}
                          >
                            <SearchIcon fontSize="small" sx={{color:theme.palette.text.secondary}}/>
                          </IconButton>

                          <Popover
                            open={Boolean(anchorEls[col.field])}
                            anchorEl={anchorEls[col.field]}
                            onClose={() => handleCloseSearch(col.field)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "center",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "center",
                            }}
                            PaperProps={{
                              sx: {
                                p: 1,
                                border:"1px solid gray",
                                borderRadius: 1.5,
                                boxShadow: 3,
                              },
                            }}
                          >
                            <Box sx={{ width: 150 }}>
                              {" "}
                              {/* Smaller width */}
                              <InputBase
                                placeholder={`Search ${col.headerName}`}
                                onChange={(e) =>
                                  handleSearchChange(col.field, e.target.value)
                                }
                                autoFocus
                                sx={{
                                  width: "100%",
                                  px: 1,
                                  py: 0.4, // Smaller height
                                  fontSize: 13, // Slightly smaller text
                                  borderRadius: 1,
                                  backgroundColor: "#fafafa",
                                  border: "1px solid #C6C6C6",
                                  height: 30, // Fixed compact height
                                  transition: "all 0.2s ease",
                                  "&:focus-within": {
                                    backgroundColor: "#fff",
                                    borderColor: theme.palette.secondary.main,
                                  },
                                }}
                              />
                            </Box>
                          </Popover>
                        </>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Spinner loading={true}/>
                </TableCell>
              </TableRow>
              ) : (
              paginatedRows.map((row, rowIndex) => (
                <TableRow
                  key={`${row[rowKey] ?? "row"
                    }-${rowIndex}-${order}-${orderBy}-${page}`}
                  hover
                  sx={{
                    backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#fff",
                    "&:hover": {
                      backgroundColor: "#eeeeee !important",
                    },
                    "&:hover > td": {
                      backgroundColor: "#eeeeee !important",
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        borderBottom: "1px solid #e0e7ef",
                        borderRight: "1px solid #f1f5f9",
                        "&:last-child": { borderRight: "none" },
                      }}
                    >
                      {col.renderCell ? col.renderCell(row) : row[col.field]}
                    </TableCell>
                  ))}
                </TableRow>
              )))
            }

            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{backgroundColor:"#fff"}}>
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalRows || sortedRows.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          backgroundColor: "#f3f3f3",
        }}
      />
    </Paper>
  );
};

export default ReactTable;



